import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Comment } from "../models/comments";
import { useUser } from "../state";
import { api } from "../api";
import { EntryReview } from "../../../src/apidefs";
import { EditButton } from "./EditButton";
import { Compo } from "../models/compos";
import { VoteJSON } from "../models/vote";
import { VoteStars } from "../Vote/VoteStars";
import { RLink } from "./RLink";
import { SmallButton } from "./SmallButton";
import { FormattedText } from "./FormattedText";
import { ShowPrompt } from "./PromptModal";
import { DateWithTooltip } from "./DateWithTooltip";
import {
  ArrowUturnLeftIcon,
  ClipboardDocumentListIcon,
  TrashIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/solid";

type ModerationEvent = {
  id: string;
  createdAt: string;
  actorUsername: string;
  actionType: string;
  reason: string | null;
};

export type CommentAnonState = "never" | "was anon" | "anon but yours";

const heartIconClass = (active: boolean): string =>
  [
    "fa fa-heart transition duration-150 ease-in-out",
    active ? "text-[#ff3c3c] hover:brightness-110 hover:scale-105" : "",
  ]
    .filter(Boolean)
    .join(" ");

export type CommentUserMinimal = {
  username: string;
  avatar_url: string;
  judge?: boolean;
};

export interface CommentViewProps {
  replyCB?: (username: string) => void;
  comment?: Comment;
  review?: EntryReview;
  compo?: Compo;
  vote?: VoteJSON;
  anonymous: "never" | "was anon" | "anon but yours";
  comment_likes: number;
  comment_user: CommentUserMinimal;
  comment_is_liked: boolean;
  author_banned?: boolean;
}

/**
 * CommentComponent shows generic comments. It is used to show
 * comments, reviews and even forum posts.
 */
export function CommentComponent({
  replyCB,
  comment,
  review,
  compo,
  vote,
  anonymous,
  comment_likes,
  comment_user: propCommentUser,
  comment_is_liked,
  author_banned: propAuthorBanned,
}: CommentViewProps): JSX.Element {
  const userState = useUser();
  const currentUser =
    userState.type === "logged-in" ? userState.user : undefined;
  const editTextbox = useRef<HTMLTextAreaElement>(null);

  const [hasLiked, setHasLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [hidden, setHidden] = useState(false);
  const [authorBanned, setAuthorBanned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [activityLog, setActivityLog] = useState<ModerationEvent[]>([]);
  const [activityLogLoading, setActivityLogLoading] = useState(false);
  const [showModMenu, setShowModMenu] = useState(false);
  const modMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let hiddenValue = false;
    if (comment) hiddenValue = comment.hidden;
    if (review) hiddenValue = review.hidden;

    setHasLiked(review ? review.has_favorited : comment_is_liked);
    setLikes(comment_likes);
    setEditing(false);
    setText((comment && comment.content) || (review && review.review) || "");
    setHidden(hiddenValue);
    setAuthorBanned(
      propAuthorBanned || (review && review.author_banned) || false
    );
  }, [comment, review, comment_likes, comment_is_liked, propAuthorBanned]);

  // Close mod menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modMenuRef.current &&
        !modMenuRef.current.contains(event.target as Node)
      ) {
        setShowModMenu(false);
      }
    };
    if (showModMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showModMenu]);

  const like = async (): Promise<void> => {
    if (comment) {
      const result = await api.post<"/api/comment/:comment_id/like">(
        `/api/comment/${comment.id}/like`
      );

      if (!api.isErrorToast(result)) {
        setHasLiked(true);
        setLikes(comment_likes + 1);
      }
    }

    if (review) {
      const vote_id = review.vote_id;
      const result = await api.post(`/api/review/${vote_id}/favorite`);

      if (!api.isErrorToast(result)) {
        setHasLiked(true);
        setLikes(review.review_favorites + 1);
      }
    }
  };

  const startEditing = (): void => {
    setEditing(true);
  };

  const saveEdits = async (): Promise<void> => {
    if (!editTextbox.current) {
      return;
    }

    if (review) {
      const reviewid = review.vote_id;
      const result = await api.post<"/api/edit/review/:id">(
        `/api/edit/review/${reviewid}`,
        {
          content: editTextbox.current.value,
        }
      );

      if (api.isErrorToast(result)) return;
    }

    if (comment) {
      const commentid = comment.id;
      const result = await api.post<"/api/edit/comment/:id">(
        `/api/edit/comment/${commentid}`,
        {
          content: editTextbox.current.value,
        }
      );

      if (api.isErrorToast(result)) return;
    }

    setEditing(false);
    setText(editTextbox.current.value);
  };

  const remove = async (reason?: string): Promise<void> => {
    if (comment) {
      await api.post<"/api/comment/:id/delete">(
        `/api/comment/${comment.id}/delete`,
        { reason }
      );

      setHidden(!hidden);
    }

    if (review) {
      await api.post<"/api/compo/judge/:id/delete">(
        `/api/compo/judge/${review.vote_id}/delete`,
        { reason }
      );

      setHidden(!hidden);
    }
  };

  const removeAsAdmin = async (): Promise<void> => {
    const itemType = comment ? "comment" : "review";
    const action = hidden ? "unhide" : "delete";
    const reason = await ShowPrompt(
      `Why are you ${action === "delete" ? "deleting" : "unhiding"} this ${itemType}?`,
      `Enter a reason for ${action === "delete" ? "deleting" : "unhiding"} this ${itemType}...`
    );

    if (reason === null) {
      return; // User cancelled
    }

    await remove(reason);
  };

  const openActivityLog = async (): Promise<void> => {
    if (!comment && !review) return;
    setShowActivityLog(true);
    setActivityLogLoading(true);
    if (comment) {
      const result = await api.get<"/api/comment/:id/moderation-log">(
        `/api/comment/${comment.id}/moderation-log`
      );
      if (!api.isToast(result)) {
        setActivityLog(result.events);
      }
    } else if (review) {
      const result = await api.get<"/api/compo/judge/:id/moderation-log">(
        `/api/compo/judge/${review.vote_id}/moderation-log`
      );
      if (!api.isToast(result)) {
        setActivityLog(result.events);
      }
    }
    setActivityLogLoading(false);
  };

  let comment_user: CommentUserMinimal = propCommentUser;
  if (review) {
    comment_user = {
      username: review.username,
      avatar_url: review.avatar_url,
      judge: review.judge,
    };
  }

  const isYours =
    currentUser &&
    ((comment && comment.user_id === currentUser.id) ||
      (review && review.username === currentUser.username));

  const commentDate = comment
    ? comment.created
    : new Date(review!.review_created);

  const isAnonButYours = anonymous === "anon but yours";
  const showRealIdentity = isAnonButYours && hovered;
  const isAnonymous =
    (comment_user.username === "Anonymous" ||
      comment_user.username === "Anonymous Chip" ||
      isAnonButYours) &&
    !showRealIdentity;
  const avatarSrc = isAnonymous
    ? "/img/anonymous-chip.png"
    : comment_user.avatar_url;
  const displayName = isAnonButYours
    ? showRealIdentity
      ? comment_user.username
      : "Anonymous Chip (you!)"
    : isAnonymous
      ? "Anonymous"
      : comment_user.username;

  return (
    <div
      className={`mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-800 ${hidden || authorBanned ? "opacity-50" : ""}`}
      onMouseEnter={() => isAnonButYours && setHovered(true)}
      onMouseLeave={() => isAnonButYours && setHovered(false)}
    >
      {/* Main content */}
      <div className="p-4">
        {/* Header with avatar and username */}
        <div className="mb-3 flex items-start gap-3">
          <RLink
            className="flex-shrink-0"
            to={isAnonymous ? "javascript:;" : "/user/" + comment_user.username}
          >
            <img
              src={avatarSrc}
              className="h-12 w-12 rounded-lg object-cover"
              alt={displayName}
            />
          </RLink>
          <div className="flex-1 pt-1">
            <div className="flex flex-col gap-0.5">
              <RLink
                to={
                  isAnonymous
                    ? "javascript:;"
                    : "/user/" + comment_user.username
                }
              >
                <span className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  {displayName}
                </span>
              </RLink>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                <DateWithTooltip date={commentDate} />
              </span>
            </div>
          </div>
        </div>

        {/* Comment body */}
        <div>
          {editing ? (
            <textarea
              ref={editTextbox}
              defaultValue={text}
              className="w-full rounded border border-slate-300 p-2 text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
            />
          ) : (
            <div
              className="text-slate-700 dark:text-slate-300"
              style={
                hidden || authorBanned
                  ? {
                      color: "lightgray",
                    }
                  : {}
              }
            >
              <FormattedText text={text} />
            </div>
          )}

          {anonymous === "was anon" && (
            <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              This comment was posted anonymously.
            </div>
          )}

          {hidden && (
            <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              This {review ? "review" : "comment"} has been deleted.
            </div>
          )}

          {authorBanned && !hidden && (
            <div className="mt-2 text-sm text-red-500 dark:text-red-400">
              The author of this {review ? "review" : "comment"} is banned.
            </div>
          )}

          {vote && (
            <div className="mt-2 text-lg text-slate-900 dark:text-white">
              Overall: <VoteStars score={vote.custom1!} />
            </div>
          )}

          {comment_user.judge && review && compo && compo.judged && (
            <small className="mt-2 block text-sm text-slate-500 dark:text-slate-400">
              {compo.state === "judging" && (
                <span>
                  This comment is invisible to everyone except compo judges.
                </span>
              )}

              {compo.state === "over" && (
                <span>This is an official judge review.</span>
              )}
            </small>
          )}
        </div>
      </div>

      {/* Footer with buttons */}
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-100 px-4 py-2 dark:border-white/10 dark:bg-slate-700">
        {!editing &&
          (!isYours ? (
            <SmallButton
              onClick={() => like()}
              variant={hasLiked ? "active" : "default"}
              badge={likes}
              icon={
                <i className={heartIconClass(hasLiked)} aria-hidden="true"></i>
              }
            >
              {hasLiked ? "Liked" : "Like"}
            </SmallButton>
          ) : (
            <SmallButton
              badge={likes}
              icon={
                <i className={heartIconClass(hasLiked)} aria-hidden="true"></i>
              }
            >
              Yours
            </SmallButton>
          ))}
        {currentUser && isYours && (
          <EditButton
            onStartEdit={() => startEditing()}
            onSave={() => saveEdits()}
          />
        )}
        {/* Delete button for your own comment/review (not a mod action) */}
        {!editing && currentUser && isYours && (comment || review) && (
          <SmallButton
            onClick={() => remove()}
            icon={<i className="fa fa-trash-o" aria-hidden="true"></i>}
          >
            {hidden ? "UnDelete" : "Delete"}
          </SmallButton>
        )}
        {!editing && (comment || review) && replyCB && (
          <SmallButton
            onClick={() => replyCB && replyCB(comment_user.username)}
            icon={<ArrowUturnLeftIcon className="h-4 w-4" />}
          >
            Reply
          </SmallButton>
        )}
        {/* Mod dropdown for admin actions on others' content */}
        {currentUser && currentUser.admin && !isYours && (
          <div className="relative" ref={modMenuRef}>
            <SmallButton
              onClick={() => setShowModMenu(!showModMenu)}
              variant="mod"
              icon={<ShieldCheckIcon className="h-4 w-4" />}
            >
              Mod
            </SmallButton>
            {showModMenu && (
              <div className="absolute bottom-full left-0 z-10 mb-1 min-w-[150px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-600 dark:bg-slate-800">
                {(comment || review) && (
                  <button
                    onClick={() => {
                      removeAsAdmin();
                      setShowModMenu(false);
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <TrashIcon className="h-4 w-4 flex-shrink-0" />
                    <span>{hidden ? "UnDelete" : "Delete"}</span>
                  </button>
                )}
                {(comment || review) && (
                  <button
                    onClick={() => {
                      openActivityLog();
                      setShowModMenu(false);
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <ClipboardDocumentListIcon className="h-4 w-4 flex-shrink-0" />
                    <span>Activity Log</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Activity Log Modal - rendered via portal to avoid inheriting opacity */}
      {showActivityLog &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowActivityLog(false)}
            />
            <div className="relative mx-4 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-slate-800">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                Moderation Activity Log
              </h2>
              <div className="mt-4">
                {activityLogLoading ? (
                  <div className="text-slate-600 dark:text-slate-300">
                    Loading...
                  </div>
                ) : activityLog.length === 0 ? (
                  <div className="text-slate-600 dark:text-slate-300">
                    No moderation activity recorded.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="py-2 pr-3 text-left font-medium text-slate-600 dark:text-slate-400">
                          Action
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-400">
                          By
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-400">
                          Time
                        </th>
                        <th className="py-2 pl-3 text-left font-medium text-slate-600 dark:text-slate-400">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityLog.map((event) => (
                        <tr
                          key={event.id}
                          className="border-b border-slate-100 dark:border-slate-700/50"
                        >
                          <td className="py-2 pr-3 text-slate-800 dark:text-slate-200">
                            {event.actionType}
                          </td>
                          <td className="px-3 py-2 text-slate-800 dark:text-slate-200">
                            {event.actorUsername}
                          </td>
                          <td className="px-3 py-2 text-slate-500 dark:text-slate-400">
                            {new Date(event.createdAt).toLocaleString()}
                          </td>
                          <td className="py-2 pl-3 text-slate-500 dark:text-slate-400">
                            {event.reason || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowActivityLog(false)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
